import first from 'lodash/first';
import sumBy from 'lodash/sumBy';
import * as React from 'react';
import verticalLayoutStyles from '../StudyModelLayout/Components/StudyModelLayout.vertical.module.css';
import type { BaseSubjectProps, StudyModelLayoutProps } from '../StudyModelLayout/StudyModelLayout';
import type { SubjectCategory } from '~/Features/StudyPage/Common/Interfaces/SubjectCategory';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { BookmarkContainer } from '~/Partials/BookmarkContainer/BookmarkContainer';
import type { BookmarkTabProps } from '~/Partials/BookmarkTabs/BookmarkTabs';
import type { TabKey } from '~/Partials/TabList/Tab';
import { firstKey } from '~/Partials/Utils/utils';
import type {
    StudyModelGridContentProps,
    StudyModelGridContentTranslations,
} from './Components/StudyModelGridContent/StudyModelGridContent';
import { StudyModelGridContent } from './Components/StudyModelGridContent/StudyModelGridContent';
import { StudyModelGridHiddenTabContent } from './Components/StudyModelGridHiddenTabContent/StudyModelGridHiddenTabContent';
import { getPointsLabel } from './Utils/studyModelGridUtils';

interface StudyModelGridTranslations extends StudyModelGridContentTranslations {
    selectYear: string;
    totalStudyPointsFormat: string;
}

export interface AvailableYearProps
    extends BookmarkTabProps,
        Pick<StudyModelLayoutProps<ExtendedSubjectProps>, 'firstHeading' | 'secondHeading'> {
    isDefault?: boolean;
}

export interface StudyOption {
    label: string;
    isDefault?: boolean;
}

export interface ExtendedSubjectProps extends BaseSubjectProps {
    name: string;
    description: HtmlString;
    category: SubjectCategory;
    year: number;
    specialization?: string;
    studyOption?: StudyOption;
    isDescriptionOutdated?: boolean;
}

export interface StudyModelGridProps
    extends Pick<
        StudyModelGridContentProps,
        'specializationOptions' | 'studyOptions' | 'getSubjectInfoUrl' | 'overlayContentMessages'
    > {
    subjects: ExtendedSubjectProps[];
    availableYears: AvailableYearProps[];
    translations: StudyModelGridTranslations;
}

export const StudyModelGrid: React.FC<StudyModelGridProps> = props => {
    const {
        availableYears,
        subjects,
        specializationOptions,
        studyOptions,
        getSubjectInfoUrl,
        overlayContentMessages,
        translations,
    } = props;
    const { selectYear, totalStudyPointsFormat, ...restTranslations } = translations;

    const [selectedSpecialization, setSelectedSpecialization] = React.useState(firstKey(specializationOptions));
    const [selectedStudyOption, setSelectedStudyOption] = React.useState(firstKey(studyOptions));
    const [selectedYear, setSelectedYear] = React.useState<TabKey | undefined>(
        (availableYears.find(year => year.isDefault) ?? first(availableYears))?.uniqueId
    );

    const selectedYearProps = React.useMemo(
        () => availableYears.find(year => year.uniqueId === selectedYear),
        [availableYears, selectedYear]
    );

    const filteredSubjects = React.useMemo(
        () =>
            subjects
                .filter(subject => !subject.specialization || subject.specialization === selectedSpecialization)
                .filter(subject => !subject.studyOption || subject.studyOption.label === selectedStudyOption),
        [subjects, selectedSpecialization, selectedStudyOption]
    );

    const currentYearSubjects = React.useMemo(() => {
        if (selectedYearProps === undefined) {
            return [];
        }

        return filteredSubjects.filter(subject => subject.year === selectedYearProps.uniqueId);
    }, [filteredSubjects, selectedYearProps]);

    const totalPoints = sumBy(
        filteredSubjects.filter(subject => subject.year > 0),
        subject => subject.points
    );

    const HiddenTabContent = React.useMemo<React.FC<BookmarkTabProps>>(
        () => tabProps => {
            const { uniqueId } = tabProps;

            return <StudyModelGridHiddenTabContent year={uniqueId} allSubjects={subjects} />;
        },
        [subjects]
    );

    if (selectedYear === undefined || !selectedYearProps) {
        return null;
    }

    return (
        <BookmarkContainer
            tabsLabel={selectYear}
            tabs={availableYears}
            currentlySelectedTab={selectedYear}
            setSelectedTab={setSelectedYear}
            upperDescription={getPointsLabel(totalStudyPointsFormat, totalPoints)}
            HiddenTabContent={HiddenTabContent}
            variant="dark"
            className={verticalLayoutStyles.ContainerWithLayoutExceedingWrapper}
        >
            <StudyModelGridContent
                getSubjectInfoUrl={getSubjectInfoUrl}
                selectedYearProps={selectedYearProps}
                currentYearSubjects={currentYearSubjects}
                specializationOptions={specializationOptions}
                selectedSpecialization={selectedSpecialization}
                onSpecializationSelected={setSelectedSpecialization}
                studyOptions={studyOptions}
                selectedStudyOption={selectedStudyOption}
                onStudyOptionSelected={setSelectedStudyOption}
                overlayContentMessages={overlayContentMessages}
                translations={restTranslations}
            />
        </BookmarkContainer>
    );
};
